import { C8NodeType, RemoveEventHandle } from "csi/typings";
import { cipher, decipher } from "csi/utility/cipherUtility";
import _ from "lodash";

const ENCODED_PREFIX = "REF=";
const ENCODED_PARAM = "REF";
const RE_HASH = /^#/;

let _encodeHash = false;
const _homeTabsByType = new Map<C8NodeType, string>();
let _navigationChecks: NavigationCheckFunction[] = [];

export const LOCATION_COMPARE_URL = "CompareURL";
export const LOCATION_DISABLE_TAB_MATCHING = "ResetTab";
export const LOCATION_FILTER = "Filter";
export const LOCATION_NOTIFICATION = "Notification";
export const LOCATION_PREVIOUS_URL = "PreviousURL";
export const LOCATION_REVISION_URL = "RURL";
export const LOCATION_RIGHT_PANE = "RightPane";
export const LOCATION_RIGHT_PANE_URL = "RPURL";
export const LOCATION_SECTION = "Section";
export const LOCATION_SECTION_ITEM = "SectionItem";
export const LOCATION_SELECTED_URL = "SURL";
export const LOCATION_SESSION_URL = "SessionURL";
export const LOCATION_TAB = "Tab";
export const LOCATION_TYPE = "Type";
export const LOCATION_URL = "URL";
export const PROPERTY_SEARCH = "search";
export const PROPERTY_HASH = "hash";
export const PROPERTY_PROTOCOL = "protocol";

export const VALID_LOCATION_KEYS = [
	LOCATION_COMPARE_URL,
	LOCATION_DISABLE_TAB_MATCHING,
	LOCATION_FILTER,
	LOCATION_NOTIFICATION,
	LOCATION_PREVIOUS_URL,
	LOCATION_REVISION_URL,
	LOCATION_RIGHT_PANE,
	LOCATION_RIGHT_PANE_URL,
	LOCATION_SECTION,
	LOCATION_SECTION_ITEM,
	LOCATION_SELECTED_URL,
	LOCATION_SESSION_URL,
	LOCATION_TAB,
	LOCATION_TYPE,
	LOCATION_URL,
];

export type LocationCallback = (value: string) => void;
export type NavigationCheckFunction = (isPageNavigation: boolean, isTabNavigation: boolean) => Promise<boolean>;

export interface LocationItem {
	/** callback function when location parameter value changes */
	callback?: LocationCallback;

	/** unique key used as location parameter */
	key: string;
};

export interface LocationItemState extends LocationItem {
	/** parameter value for key */
	value?: string;
};

/** sort location entries alphabetically, except ensure URL comes first */
function compareLocationToken(a: [string, any], b: [string, any]): number {
	const key1 = a[0];
	const key2 = b[0];

	if (key1 === LOCATION_URL) {
		return -1;
	}

	if (key2 === LOCATION_URL) {
		return 1;
	}

	if (key1 < key2) {
		return -1;
	}

	if (key1 < key2) {
		return 1;
	}

	return 0;
}

/** decipher the value of a specific object key */
export function decipherOnRefKey(hash: string): string {
	let params = convertHashToObject(hash);

	const { REF, ...remainingParams } = params;

	if (REF) {
		const decipheredRef = convertHashToObject(decipher(REF));

		params = {
			...decipheredRef,
			...remainingParams,
		};
	}

	return convertObjectToHash(params);
}

/** decode hash if encoded */
export function decodeHash(hash: string): string {
	// Ensure there is no leading hash
	hash = stripHash(hash);

	if (isHashEncoded(hash)) {
		if (_encodeHash) {
			return decipherOnRefKey(hash);
		} else {
			// do not decode unless encoding is on - if encoded hash is encountered, then just return empty
			return "#";
		}
	}

	return `#${hash}`;
}

/** encode hash using cipher */
export function encodeHash(hash: string): string {
	hash = stripHash(hash);

	if (_encodeHash && !isHashEncoded(hash)) {
		return `#${ENCODED_PREFIX}${cipher(hash)}`;
	}

	return `#${hash}`;
}

/** return true if hash is encoded */
export function isHashEncoded(hash: string): boolean {
	return hash.includes(ENCODED_PREFIX);
}

/** strip hash of # symbol */
export function stripHash(hash: string): string {
	return hash.replace(RE_HASH, "");
}

/** return hash from browser location without "#" symbol */
export function captureHash(): string {
	return location.hash;
}

/** set location hash */
export function setLocationHash(hash: string): void {
	location.hash = encodeHash(hash);
}

/** returns a Record (key value object) when passed in a URL hash  */
export function convertHashToObject(hash: string): Record<string, string> {
	return stripHash(hash)
		.split("&")
		.reduce((res, param) => {
			const [key, ...values] = param.split("=");
			const value = values.join("=");
			const decodedKey = decodeURIComponent(key);
			const decodedValue = (key === ENCODED_PARAM) ? value : decodeURIComponent(value);

			return {
				...res,
				[decodedKey]: decodedValue,
			};
		}, {} as Record<string, string>);
}

/** returns a hash when given an object */
export function convertObjectToHash(params: object): string {
	const entries = Object.entries(params);

	// sort hash parameters for consistent ordering
	entries.sort(compareLocationToken);

	// convert entries to hash
	const tokens = entries.map(([key, value]) => getLocationToken(key, value));
	return "#" + tokens.filter(Boolean)
		.join("&");
}

/** return object with parameters from given hash, or browser hash if none provided */
export function decomposeHash(hash = captureHash()): Record<string, string> {
	const tokenString = decodeHash(hash);
	const obj = convertHashToObject(tokenString);

	// decode '+' as ' ' in URL parameters - this seems to be core issue in URL encoding in Java
	const decodePlusForUrl = (key: string): void => {
		if (obj[key]) {
			obj[key] = obj[key].replace(/\+/g, " ");
		}
	};

	decodePlusForUrl(LOCATION_PREVIOUS_URL);
	decodePlusForUrl(LOCATION_REVISION_URL);
	decodePlusForUrl(LOCATION_RIGHT_PANE_URL);
	decodePlusForUrl(LOCATION_SELECTED_URL);
	decodePlusForUrl(LOCATION_URL);

	return obj;
}

/** return tab at home level to navigate to when instance of that type is deleted when user is on that node */
export function getHomeTabByType(type: C8NodeType): string | undefined {
	return _homeTabsByType.get(type);
}

/** return location hash for given parameters */
export function getLocationHash(params: object): string {
	const hash = convertObjectToHash(params);
	return encodeHash(hash);
}

/**
 * Return location hash for url and optional params
 */
export function getLocationForUrl(url: string, params?: object): string {
	params = params || {};
	params[LOCATION_URL] = url || params[LOCATION_URL];
	return getLocationHash(params);
}

/**
 * Return numbered key
 */
export function getLocationKey(key: string, index?: number): string {
	return `${key}${index || ""}`;
}

/**
 * Return string for location token given key/value pair
 */
export function getLocationToken(key: string, value: string): string {
	if (key && (value != null)) {
		const encodedKey = encodeURIComponent(key);
		const encodedValue = (key === ENCODED_PARAM) ? value : encodeURIComponent(value);
		return `${encodedKey}=${encodedValue}`;
	}

	return "";
}

/**
 * Replace last history entry with given hash
 */
export function replaceHash(hash: string): void {
	const url = new URL(location.href);
	url.hash = encodeHash(hash);
	history.replaceState(null, null, url.toString());
}

/**
 *Returns a parameter of the URL object
 */
export function getUrlProperty(url: string, propertyName: string): string {
	try {
		return new URL(url)[propertyName];
	} catch (e) {
		return undefined;
	}
}

/**
 * Returns the search parameters and values from a given location url
 */
export function getUrlSearchParameters(url: string): Record<string, string> | null {
	if (!url) {
		return null;
	}

	// The question mark at the start of the search string needs to be removed
	const search = getUrlProperty(url, PROPERTY_SEARCH)
		.substring(1);
	return decomposeHash(search);
}

/** add http protocol to given url if missing */
export function addHttpProtocolIfMissing(url: string): string {
	if (!url) {
		return "";
	}
	if (getUrlProperty(url, PROPERTY_PROTOCOL)) {
		return url;
	}
	return "http://" + url;
}

/** call navigation check functions before navigation occurs to determine if navigation should be allowed */
export async function checkNavigation(isPageNavigation: boolean, isTabNavigation: boolean): Promise<boolean> {
	for (const checkFunction of _navigationChecks) {
		const result = await checkFunction(isPageNavigation, isTabNavigation);
		if (!result) {
			return false;
		}
	}

	return true;
}

/** register tab at home level by type - this view is navigated to when node of that type is deleted while on its page */
export function registerHomeTab(type: C8NodeType, tabId: string): void {
	_homeTabsByType.set(type, tabId);
}

/** register function to check if navigation should be allowed, returning handle to remove check */
export function registerNavigationCheck(checkFunction: NavigationCheckFunction): RemoveEventHandle {
	_navigationChecks.push(checkFunction);

	return {
		remove: (): void => {
			_navigationChecks = _.without(_navigationChecks, checkFunction);
		},
	};
}

/** if enabled, the location hash will be encoded so that any parameters are obfuscated */
export function setLocationHashEncodingEnabled(enabled: boolean): void {
	_encodeHash = enabled;
}
